/**
 * Empty state shown on the Backtesting page when the user has no strategies yet.
 * Strategies are created exclusively through the user's connected agent (Claude Code,
 * Codex, Cursor) — never inside this UI. This component points the user back there.
 */

import { useState } from 'react';

export const STARTER_PROMPT =
  'How do I create my first strategy? Walk me through it step by step.';

interface BacktestingEmptyStateProps {
  onOpenConnectionGuide: () => void;
}

export function BacktestingEmptyState({ onOpenConnectionGuide }: BacktestingEmptyStateProps) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(STARTER_PROMPT).catch(() => {
        // Clipboard write failed — still show the "Copied" state optimistically
      });
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="min-h-full grid place-items-center px-6 py-10">
      <div className="w-full max-w-[600px] text-center reveal">

        {/* Listening pill */}
        <div className="inline-flex items-center gap-2 mb-[30px]">
          <span
            className="relative"
            style={{ width: 7, height: 7, display: 'inline-block' }}
          >
            <span
              className="absolute inset-0 rounded-full"
              style={{ background: 'var(--aqua)' }}
            />
            <span
              className="absolute rounded-full border"
              style={{
                inset: '-5px',
                borderColor: 'var(--aqua)',
                opacity: 0.5,
                animation: 'es-ping 1.9s cubic-bezier(0,0,.2,1) infinite',
              }}
            />
          </span>
          <span
            className="font-mono text-primary-400 font-semibold uppercase tracking-[0.13em]"
            style={{ fontSize: 11 }}
          >
            Listening for your first strategy
          </span>
        </div>

        {/* Journey rail */}
        <div className="flex items-center justify-center mb-[34px]" aria-label="workflow steps">
          {/* Strategy (active) */}
          <div className="flex flex-col items-center gap-[9px]">
            <span
              className="rounded-full"
              style={{
                width: 11,
                height: 11,
                border: '1.5px solid var(--aqua)',
                background: 'var(--aqua)',
                boxShadow: '0 0 0 4px rgba(18,189,161,.14)',
              }}
            />
            <span className="font-sans font-semibold text-primary-100" style={{ fontSize: 11 }}>
              Strategy
            </span>
          </div>

          {/* Connector lit */}
          <span
            className="mx-1 shrink-0"
            style={{
              width: 54,
              height: 1.5,
              marginBottom: 23,
              background: 'linear-gradient(90deg, var(--aqua), var(--g700, #222b3c))',
              display: 'block',
            }}
          />

          {/* Backtest (dimmed) */}
          <div className="flex flex-col items-center gap-[9px]">
            <span
              className="rounded-full"
              style={{
                width: 11,
                height: 11,
                border: '1.5px solid #222b3c',
                background: 'var(--ink-900)',
              }}
            />
            <span className="font-sans font-semibold text-gray-500" style={{ fontSize: 11 }}>
              Backtest
            </span>
          </div>

          {/* Connector dimmed */}
          <span
            className="mx-1 shrink-0"
            style={{
              width: 54,
              height: 1.5,
              marginBottom: 23,
              background:
                'repeating-linear-gradient(90deg,#222b3c 0 5px, transparent 5px 9px)',
              display: 'block',
            }}
          />

          {/* Optimize (dimmed) */}
          <div className="flex flex-col items-center gap-[9px]">
            <span
              className="rounded-full"
              style={{
                width: 11,
                height: 11,
                border: '1.5px solid #222b3c',
                background: 'var(--ink-900)',
              }}
            />
            <span className="font-sans font-semibold text-gray-500" style={{ fontSize: 11 }}>
              Optimize
            </span>
          </div>

          {/* Connector dimmed */}
          <span
            className="mx-1 shrink-0"
            style={{
              width: 54,
              height: 1.5,
              marginBottom: 23,
              background:
                'repeating-linear-gradient(90deg,#222b3c 0 5px, transparent 5px 9px)',
              display: 'block',
            }}
          />

          {/* Deploy (dimmed) */}
          <div className="flex flex-col items-center gap-[9px]">
            <span
              className="rounded-full"
              style={{
                width: 11,
                height: 11,
                border: '1.5px solid #222b3c',
                background: 'var(--ink-900)',
              }}
            />
            <span className="font-sans font-semibold text-gray-500" style={{ fontSize: 11 }}>
              Deploy
            </span>
          </div>
        </div>

        {/* Headline */}
        <h1
          className="font-display font-extrabold tracking-tight mb-3"
          style={{ fontSize: 30, lineHeight: 1.12, letterSpacing: '-0.02em' }}
        >
          It all starts with a{' '}
          <em
            className="not-italic"
            style={{
              background: 'linear-gradient(100deg, var(--aqua), #9af3e1)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            strategy
          </em>
          .
        </h1>

        {/* Sub-line */}
        <p
          className="mx-auto mb-[30px] text-gray-400"
          style={{ maxWidth: '42ch', fontSize: 14.5, lineHeight: 1.6 }}
        >
          Backtests, grid search and bots all build on one — and your connected agent creates
          it. Just ask, and it shows up here.
        </p>

        {/* Terminal-style starter prompt card */}
        <div
          className="text-left rounded-[13px] overflow-hidden"
          style={{
            border: '1px solid #222b3c',
            background: 'rgba(6,9,15,.6)',
            boxShadow: '0 24px 60px -34px rgba(0,0,0,.85)',
          }}
        >
          {/* Window bar */}
          <div
            className="flex items-center gap-[7px] px-[13px] py-[9px]"
            style={{
              borderBottom: '1px solid #222b3c',
              background: 'rgba(124,137,166,.03)',
            }}
          >
            <span className="rounded-full" style={{ width: 9, height: 9, background: '#333e54' }} />
            <span className="rounded-full" style={{ width: 9, height: 9, background: '#333e54' }} />
            <span className="rounded-full" style={{ width: 9, height: 9, background: '#333e54' }} />
            <span
              className="font-mono text-gray-500 font-semibold ml-[6px]"
              style={{ fontSize: 11 }}
            >
              your agent · Claude Code · Codex · Cursor
            </span>
          </div>

          {/* Prompt body */}
          <div className="flex items-start gap-[13px] px-[15px] py-[15px]">
            <div
              className="flex-1 font-mono text-[#cfe9e2] font-medium"
              style={{ fontSize: 13.5, lineHeight: 1.6 }}
            >
              <span className="text-primary-400 mr-[7px]">›</span>
              {STARTER_PROMPT}
            </div>
            <button
              onClick={handleCopy}
              aria-label={copied ? 'Copied' : 'Copy'}
              className="shrink-0 inline-flex gap-[7px] items-center font-sans font-bold rounded-[9px] transition-all duration-150 whitespace-nowrap"
              style={
                copied
                  ? {
                      fontSize: 12,
                      color: 'var(--aqua)',
                      padding: '9px 13px',
                      border: 'none',
                      background: 'rgba(18,189,161,.16)',
                      boxShadow: 'none',
                      cursor: 'default',
                    }
                  : {
                      fontSize: 12,
                      color: '#06231f',
                      padding: '9px 13px',
                      border: 'none',
                      background: 'linear-gradient(120deg,#3ee0c4,#16c7a9)',
                      boxShadow: '0 8px 18px -9px rgba(18,189,161,.7)',
                      cursor: 'pointer',
                    }
              }
            >
              {copied ? (
                <>
                  {/* Checkmark SVG */}
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Copied
                </>
              ) : (
                <>
                  {/* Copy SVG */}
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <rect x="9" y="9" width="11" height="11" rx="2" />
                    <path d="M5 15V5a2 2 0 012-2h10" />
                  </svg>
                  Copy
                </>
              )}
            </button>
          </div>
        </div>

        {/* Caption */}
        <p className="mt-[13px] text-gray-500 font-medium" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
          Paste it into your agent to get going ·{' '}
          <button
            onClick={onOpenConnectionGuide}
            className="text-gray-400 hover:text-primary-400 transition-colors"
            style={{
              background: 'none',
              border: 'none',
              borderBottom: '1px dashed #333e54',
              cursor: 'pointer',
              padding: 0,
              font: 'inherit',
            }}
          >
            Connection guide
          </button>
        </p>

      </div>

      {/* Keyframe for the pulsing dot — injected inline since it's component-scoped */}
      <style>{`
        @keyframes es-ping {
          0% { transform: scale(.6); opacity: .6; }
          80%, 100% { transform: scale(1.9); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
